import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Talent } from "@shared/schema";
import { Copy, Check } from "lucide-react";

const inviteSchema = z.object({
  inviteEmail: z.string().email("Enter a valid email"),
  accessLevel: z.enum(["viewer", "commenter", "contributor", "editor", "manager"]),
  scopeMode: z.enum(["all", "specific"]).default("all"),
  talentIds: z.array(z.string()).default([]),
  expiresAt: z.string().optional().or(z.literal("")),
});

type InviteValues = z.infer<typeof inviteSchema>;

interface CollaboratorInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ACCESS_LEVEL_HINTS: Record<string, string> = {
  viewer: "Read-only. Can see drafts, media, and accounts but cannot change anything.",
  commenter: "Viewer + can leave comments / change requests on posts.",
  contributor: "Can upload media and draft posts. Cannot publish or delete others' work.",
  editor: "Full editing rights on posts and media. Can submit posts for approval.",
  manager: "Editor + can approve, schedule, and invite other collaborators.",
};

export function CollaboratorInviteDialog({ open, onOpenChange }: CollaboratorInviteDialogProps) {
  const { toast } = useToast();
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: talent } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
    enabled: open,
  });

  const form = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      inviteEmail: "",
      accessLevel: "contributor",
      scopeMode: "all",
      talentIds: [],
      expiresAt: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        inviteEmail: "",
        accessLevel: "contributor",
        scopeMode: "all",
        talentIds: [],
        expiresAt: "",
      });
      setInviteUrl(null);
      setCopied(false);
    }
  }, [open, form]);

  const submit = useMutation({
    mutationFn: async (values: InviteValues) => {
      const talentScope =
        values.scopeMode === "all"
          ? { mode: "all" as const }
          : { mode: "specific" as const, ids: values.talentIds };
      const payload = {
        inviteEmail: values.inviteEmail.trim(),
        accessLevel: values.accessLevel,
        module: "social_media",
        talentScope,
        expiresAt: values.expiresAt ? new Date(values.expiresAt).toISOString() : null,
      };
      const res = await apiRequest("POST", "/api/social/grants", payload);
      return res.json();
    },
    onSuccess: (grant: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/social/grants"] });
      const url = `${window.location.origin}/collaborator/accept?token=${grant.inviteToken}`;
      setInviteUrl(url);
      toast({
        title: "Invite created",
        description: "Copy the link below and share it with the collaborator.",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Could not create invite",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: InviteValues) => submit.mutate(values);

  const onCopy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const scopeMode = form.watch("scopeMode");
  const accessLevel = form.watch("accessLevel");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid="dialog-collaborator-invite">
        <DialogHeader>
          <DialogTitle>Invite an outside collaborator</DialogTitle>
          <DialogDescription>
            They will only see the Social Media module — never the rest of your CRM. Every action
            they take is logged.
          </DialogDescription>
        </DialogHeader>

        {inviteUrl ? (
          <div className="space-y-4" data-testid="block-invite-url">
            <div className="rounded-md border bg-muted/30 p-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Invite link
              </div>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={inviteUrl}
                  className="font-mono text-xs"
                  data-testid="input-invite-url"
                />
                <Button type="button" variant="outline" size="sm" onClick={onCopy} data-testid="button-copy-invite">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Share this link with the collaborator. It is single-use and tied to the email above.
            </p>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)} data-testid="button-close-invite">
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="inviteEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="editor@agency.com"
                        data-testid="input-invite-email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="accessLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Access level</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="select-access-level">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="viewer">Viewer</SelectItem>
                        <SelectItem value="commenter">Commenter</SelectItem>
                        <SelectItem value="contributor">Contributor</SelectItem>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-xs">
                      {ACCESS_LEVEL_HINTS[accessLevel]}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="scopeMode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Talent scope</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="select-scope-mode">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="all">All talent</SelectItem>
                        <SelectItem value="specific">Specific talent only</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {scopeMode === "specific" && (
                <FormField
                  control={form.control}
                  name="talentIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pick talent</FormLabel>
                      <div className="space-y-2 rounded-md border p-3 max-h-48 overflow-auto" data-testid="list-talent-checkboxes">
                        {(talent || []).length === 0 ? (
                          <div className="text-xs text-muted-foreground">No talent yet.</div>
                        ) : (
                          (talent || []).map((e) => {
                            const checked = field.value.includes(e.id);
                            return (
                              <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(c) => {
                                    if (c) field.onChange([...field.value, e.id]);
                                    else field.onChange(field.value.filter((id) => id !== e.id));
                                  }}
                                  data-testid={`checkbox-talent-${e.id}`}
                                />
                                <span>{e.name}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="expiresAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expires (optional)</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" data-testid="input-expires-at" {...field} />
                    </FormControl>
                    <FormDescription>Leave blank for no expiration.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  data-testid="button-cancel-invite"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submit.isPending} data-testid="button-create-invite">
                  {submit.isPending ? "Creating..." : "Create invite"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
